'use client';

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentProps, ComponentType, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2Icon, MoreHorizontal } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { NodeToolbar as NodeToolbarRaw, Position, useReactFlow, useStore } from '@xyflow/react';
import { selectNodeSelectedById } from '../store-selectors';
import { useIsMultiSelect } from '@/components/canvas/utils/multi-select-store';
import { useNodeOperations } from '@/providers/node-operations';
import { useI18n } from '@/providers/i18n';
import { useAuth, useUser } from '@clerk/nextjs';
import { useProject } from '@/providers/project';
import { toast } from 'sonner';
import { handleError } from '@/lib/error/handle';
import { autoGenerateAfterSave } from '@/lib/project-save';

/**
 * 图像/视频/音频节点顶部工具栏共用的样式与小组件。
 * 各工具栏的视觉语言必须保持一致，新增样式请改这里而不是在各自文件里复制。
 */

/** 工具栏外层表面 */
export const TOOLBAR_SURFACE_CLS =
  'fm-toolbar-surface flex flex-col gap-1 p-1 rounded-xl bg-sidebar/95 backdrop-blur-xl border border-sidebar-border shadow-none';

/** 工具栏弹层表面（模型/参数选择、更多菜单等共用） */
export const TOOLBAR_POPOVER_CONTENT_CLS =
  'fm-toolbar-surface z-[200] w-[260px] p-2.5 bg-sidebar/95 backdrop-blur-2xl border border-sidebar-border shadow-none rounded-xl';

/** 工具栏触发按钮（中文窄 / 其他语言宽） */
export const getToolbarBtnCls = (isZh: boolean) =>
  cn(
    'h-8 px-2.5 text-[11px] rounded-lg justify-start gap-1.5 hover:bg-sidebar-accent transition-all font-medium text-foreground/80 hover:text-foreground group',
    isZh ? 'w-[105px]' : 'w-[150px]',
  );

/** 弹层小节标题 */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/50">
      {children}
    </div>
  );
}

type ToolbarActionButtonProps = {
  icon: ComponentType<{ size?: number; className?: string }>;
  label: string;
  loading?: boolean;
} & ComponentProps<typeof Button>;

/** 工具栏按钮（图标 + 文案），forwardRef 以便用作 Popover/Dialog 触发器 */
export const ToolbarActionButton = forwardRef<HTMLButtonElement, ToolbarActionButtonProps>(
  ({ icon: Icon, label, loading, ...props }, ref) => (
    <Button ref={ref} variant="ghost" size="sm" aria-label={label} {...props}>
      {loading ? (
        <Loader2Icon size={14} className="shrink-0 animate-spin text-primary" />
      ) : (
        <Icon size={14} className="shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
      )}
      <span className="truncate">{label}</span>
    </Button>
  ),
);
ToolbarActionButton.displayName = 'ToolbarActionButton';

type ToolListItemProps = {
  selected?: boolean;
  loading?: boolean;
} & ComponentProps<typeof Button>;

/** 弹层内列表项（模型/动作通用）：左侧圆点，选中态高亮，loading 时圆点换转圈 */
export function ToolListItem({ selected, loading, className, children, ...props }: ToolListItemProps) {
  return (
    <Button
      size="sm"
      variant="ghost"
      className={cn(
        'justify-start h-9 px-2.5 rounded-lg border transition-all text-xs font-medium',
        selected
          ? 'bg-primary/10 border-primary/20 text-primary shadow-sm'
          : 'bg-sidebar-accent/30 border-transparent hover:bg-sidebar-accent',
        className,
      )}
      {...props}
    >
      {loading ? (
        <Loader2Icon size={14} className="mr-2 animate-spin" />
      ) : (
        <div className={cn('size-1.5 rounded-full mr-2', selected ? 'bg-primary animate-pulse' : 'bg-muted-foreground/20')} />
      )}
      {children}
    </Button>
  );
}

/** 弹层内单行等宽分段选择（分辨率/画质档位） */
export function SegmentedRow({
  options,
  value,
  onChange,
}: {
  options: string[];
  value?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="mb-2 flex items-center gap-1">
      {options.map((opt) => (
        <Button
          key={opt}
          size="sm"
          variant="ghost"
          className={cn(
            'flex-1 h-8 text-xs font-medium rounded-lg border transition-all',
            value === opt
              ? 'bg-primary/10 border-primary/20 text-primary shadow-sm'
              : 'bg-sidebar-accent/30 border-transparent hover:bg-sidebar-accent',
          )}
          onClick={() => onChange(opt)}
        >
          {opt}
        </Button>
      ))}
    </div>
  );
}

/** 弹层底部右对齐确认按钮 */
export function ConfirmFooter({
  disabled,
  onConfirm,
  children,
}: {
  disabled?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div className="mt-1 flex justify-end">
      <Button
        size="sm"
        className="rounded-lg px-3 h-8 text-xs font-bold shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all active:scale-95"
        disabled={disabled}
        onClick={onConfirm}
      >
        {children ?? t('Confirm')}
      </Button>
    </div>
  );
}

/**
 * 顶部工具栏显隐：可见后短暂禁用指针事件（150ms），避免选中节点瞬间误点工具栏。
 */
function useTopToolbarVisibility(visible: boolean) {
  const [shown, setShown] = useState(false);
  const [pointerEnabled, setPointerEnabled] = useState(true);
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (visible) {
      setShown(true);
      setPointerEnabled(false);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setPointerEnabled(true), 150);
    } else {
      setShown(false);
      setPointerEnabled(true);
      if (timer.current) window.clearTimeout(timer.current);
    }
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [visible]);
  return { shown, pointerEnabled };
}

/**
 * 顶部工具栏统一状态：节点选中订阅 + 多选判断 + 显隐/指针延迟。
 * 图像/视频/音频三个顶部工具栏共用，避免各自重复订阅逻辑。
 * hasMedia 为节点是否有可操作的媒体 URL。
 */
export function useTopToolbarState(nodeId: string, hasMedia: boolean) {
  const selectIsSelected = useMemo(() => selectNodeSelectedById(nodeId), [nodeId]);
  const isSelected = useStore(selectIsSelected);
  const isMultiSelect = useIsMultiSelect();
  const { shown, pointerEnabled } = useTopToolbarVisibility(
    Boolean(isSelected && hasMedia && !isMultiSelect)
  );
  return { isSelected, isMultiSelect, shown, pointerEnabled };
}

/** 顶部工具栏外壳：统一 NodeToolbar 的位置、层级与显隐样式。 */
export function TopToolbarShell({
  shown,
  pointerEnabled,
  children,
}: {
  shown: boolean;
  pointerEnabled: boolean;
  children: ReactNode;
}) {
  return (
    <NodeToolbarRaw
      isVisible={shown}
      position={Position.Top}
      align="center"
      className={`fm-node-toolbar-top z-[120] -translate-y-3 md:-translate-y-4 flex items-center gap-0 rounded-none bg-transparent p-0 shadow-none transition-opacity ${pointerEnabled ? '' : 'pointer-events-none'}`}
    >
      {children}
    </NodeToolbarRaw>
  );
}

/**
 * 在当前节点右侧创建相邻节点并连线，随后触发自动生成。
 * text 节点生成延迟更短；其余类型与历史行为一致。
 */
export function useCreateNeighborNode(nodeId: string) {
  const { t } = useI18n();
  const { getNode, addEdges, setNodes } = useReactFlow();
  const { addNode } = useNodeOperations();
  return useCallback(
    (type: string, modelId: string, extraData?: Record<string, any>) => {
      try {
        const nodeRef = getNode(nodeId) as any;
        if (!nodeRef) return;
        const width = (nodeRef.measured?.width ?? 300) as number;
        const position = {
          x: (nodeRef.position?.x ?? 0) + width + 120,
          y: nodeRef.position?.y ?? 0,
        };
        const newId = addNode(type, {
          position,
          selected: true,
          data: { viewMode: 'transform', model: modelId, ...(extraData || {}) },
        });
        requestAnimationFrame(() => {
          addEdges({
            id: `${nodeId}-${newId}-edge`,
            source: nodeId,
            target: newId,
            type: 'animated',
          } as any);
          try {
            setNodes((prev: any[]) => prev.map((n: any) => ({ ...n, selected: String(n.id) === String(newId) })));
          } catch {}
          try {
            if (type === 'text') void autoGenerateAfterSave(newId, 800);
            else setTimeout(() => { void autoGenerateAfterSave(newId, 1500); }, 150);
          } catch {}
        });
      } catch (error) {
        handleError(t('Request failed'), error);
      }
    },
    [nodeId, getNode, addEdges, setNodes, addNode, t],
  );
}

/** 「更多」菜单：复制下载链接 / 复制节点信息（画布、节点、组织、用户 ID） */
export function MoreToolPopover({
  nodeId,
  url,
  disabled,
  triggerCls,
}: {
  nodeId: string;
  url?: string;
  disabled?: boolean;
  triggerCls: string;
}) {
  const { t } = useI18n();
  const { orgId } = useAuth();
  const { user } = useUser();
  const project = useProject();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <ToolbarActionButton icon={MoreHorizontal} label={t('More')} className={triggerCls} disabled={disabled} />
      </PopoverTrigger>
      <PopoverContent align="start" className={TOOLBAR_POPOVER_CONTENT_CLS}>
        <SectionLabel>{t('More')}</SectionLabel>
        <div className="flex flex-col gap-1">
          <ToolListItem
            onClick={async () => {
              try {
                const u = String(url || '');
                if (!u) { try { toast.error(t('No URL')); } catch {} return; }
                await navigator.clipboard.writeText(u);
                try { toast.success(t('Copied')); } catch {}
              } catch {}
            }}
          >
            {t('Copy download link')}
          </ToolListItem>
          <ToolListItem
            onClick={async () => {
              try {
                const info = [
                  `projectId: ${String(project?.id ?? '')}`,
                  `nodeId: ${String(nodeId)}`,
                  `orgId: ${String((project as any)?.orgId ?? orgId ?? '')}`,
                  `userId: ${String(user?.id ?? '')}`,
                ].join('\n');
                await navigator.clipboard.writeText(info);
                try { toast.success(t('Copied')); } catch {}
              } catch {}
            }}
          >
            {t('Copy node info')}
          </ToolListItem>
        </div>
      </PopoverContent>
    </Popover>
  );
}
